import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import App from "./App";
import "../../../index.css";
import { initI18n } from "../../../packages/i18n/src/config";
import { AuthProvider } from "react-oidc-context";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store";
import { logout } from "./store/auth";
import {
  authenticateUser,
  handleAuthenticationError,
} from "@surgia/api/src/shared/user";
import { Toaster } from "sonner";
import { getKeycloakTokens } from "./utils/keyclock";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const i18n = initI18n();

const oidcConfig = {
  authority: import.meta.env.VITE_KEYCLOAK_ISSUER,
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,
  onSigninCallback: async () => {
    window.history.replaceState({}, document.title, window.location.pathname);
    const keycloakTokens = getKeycloakTokens();

    try {
      const authResult = await authenticateUser(keycloakTokens);

      // Handle access denied cases
      if (authResult.errorMessageKey == "Access denied") {
        store.dispatch(logout());
        localStorage.removeItem("token");
        sessionStorage.clear();
        window.location.href = `/wrong-portal`;
        return;
      } else if (!authResult?.hasAccess) {
        handleAuthenticationError(store, logout);
        const keycloakLogoutUrl = `${import.meta.env.VITE_KEYCLOAK_ISSUER}/protocol/openid-connect/logout?client_id=${import.meta.env.VITE_KEYCLOAK_CLIENT_ID}&post_logout_redirect_uri=${window.location.origin}`;
        window.location.href = keycloakLogoutUrl;
        return;
      }

      // Prepare auth data for Redux
      const authDataPayload = {
        ...keycloakTokens,
        userInfo: {
          email: authResult.userInfo?.user_info?.email,
          first_name: authResult.userInfo?.user_info?.first_name,
          last_name: authResult.userInfo?.user_info?.last_name,
          full_name: authResult.userInfo?.user_info?.full_name,
          subject_id: authResult.userInfo?.user_info?.subject_id,
          creation_date: authResult.userInfo?.user_info?.creation_date,
          user_permissions: authResult.userInfo?.user_permissions,
          user_branches: authResult.userInfo?.user_info?.user_branches,
        },
        isAuthenticated: true,
      };

      // Store in sessionStorage for App.tsx to pick up
      sessionStorage.setItem(
        "pendingAuthData",
        JSON.stringify(authDataPayload)
      );

      // Redirect to stored path or dashboard
      const storedPath = sessionStorage.getItem("redirectPath");
      if (storedPath) {
        sessionStorage.removeItem("redirectPath");
        window.location.href = storedPath;
      } else {
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Authentication error:", error);
    }
  },
  onSignoutCallback: () => {
    store.dispatch(logout());
    localStorage.clear();
    sessionStorage.clear();
  },
  automaticSilentRenew: true,
  silent_redirect_uri: import.meta.env.VITE_SILENT_SSO_URL,
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider {...oidcConfig}>
              <App />
              <Toaster position="top-right" richColors={true} duration={2000} />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </I18nextProvider>
    </PersistGate>
  </Provider>
);




